import Image from 'next/image'
import allAlerts from '@/config/alerts.json'
import Button from '@/components/Button'
import { CartItem as Item } from '@/contexts/CartReducer'
import GenderMap from '@/utils/GenderMap'
import { PersonalizeURLBuilder } from '@/utils/URLBuilder'
import { useRouter } from 'next/router'
import { useCartDispatch, useCartState } from '@/contexts/CartContext'
import { toast } from 'react-hot-toast'
import CustomToast from '../CustomToast'
import CartItemDisclosure from './CartItemDisclosure'
import language_translations, { LanguageTranslations } from '@/config/language'
import { v4 as uuidv4 } from 'uuid'
import NotebookDetailsModal from '../Personalize/NotebookDetailsModal'
import React from 'react'
import { addItem, removeItem } from 'CartActions'
import { usePersonalizeModal } from '@/contexts/PersonalizeModalContext';
import GetSlugs from '@/services/api/products/GetSlugs'
import { GetStaticProps } from 'next'
import GetBySlug from '@/services/api/products/GetBySlug'
import { GetSettings } from '@/services/api/settings'
import { GetDocumentInputs } from '@/services/api/personalize/GetDocuments'
import {ParsedUrlQuery} from 'querystring'
import { useQuery } from 'react-query'
import { useNotebookModal } from '@/contexts/NotebookModalContext'
import { GetDedicationImage } from '@/services/api/personalize/DedicationImage'
import { 
  PriceIcon,
  EditIcon,
  DeleteIcon,
  CrossIcon
} from '@/components/Icons/CartItemIcons'
import { useTranslation } from 'next-i18next'
import { useCurrency } from '../DesktopHeader'
import useUserPreferences from '@/hooks/useUserPreferences'
import { useAIPersonalizeModal } from '@/contexts/AIPersonalizeModalContext';
import { track } from '@/lib/tracker'

interface IParams extends ParsedUrlQuery {
  slug: string
}

export const getStaticPaths = async () => {
  try {
    const arr: string[] = await GetSlugs()
    const paths = arr.map(slug => {
      return {
        params: {slug},
      }
    })
    return {paths, fallback: 'blocking'}
  } catch (error) {
    return {paths: [], fallback: 'blocking'} 
  }
}

export const getStaticProps: GetStaticProps = async context => {
  const {slug} = context.params as IParams;
  const locale = context.locale ?? 'en'
  
  // Use default currency/country for static generation
  const defaultCurrency = 'ILS';
  const defaultCountryCode = 'IL';
  
  try {
    const [product, settings, inputs] = await Promise.all([
      GetBySlug(slug, defaultCurrency, defaultCountryCode),
      GetSettings(defaultCurrency,defaultCountryCode),
      GetDocumentInputs(slug),
    ]);
    return {props: {product, settings, inputs}, revalidate: 10};
  } catch (error) {
    console.error('Error fetching product data:', error);
    return {
      props: {
        product: null,
        settings: null,
        inputs: null,
      },
      revalidate: 10,
    };
  }
}

interface Props {
  item: Item
  index: number,
  isItemInFloatingCart: boolean,
  inputs?:string[],
  settings?: {
    image_price?: {
      amount?: number
      currency?: string
    }
  }
}

export default function CartItem({ item, index, isItemInFloatingCart, inputs, settings }: Props) {
  const {t: tCart} = useTranslation('cart');
  const cart = useCartState()
  const preferences = useUserPreferences();
  const { data: settingsInCart, isLoading, error } = useQuery(
      ['settings', preferences?.countryCode, preferences?.currencyCode],
      () => GetSettings(preferences?.countryCode, preferences?.currencyCode)
  )

  const preferencesCurrency = useCurrency(preferences?.currencyCode || cart.currency);
  const cartCurrency = useCurrency(cart.currency);
  
  // Use user's currency preference or fallback to cart currency
  const currencySymbol = preferences?.currencyCode ? 
  preferencesCurrency.symbol : 
  cartCurrency.symbol;
  
  const {locale} = useRouter();
  const name='';
  const [showPreview, setShowPreview] = React.useState(false);
  const [signedUrl, setSignedUrl] = React.useState<string | null>(null);
  const [fetchingImage, setFetchingImage] = React.useState(false);

  const processedKey = localStorage.getItem(`dedicationImageUrl-${item.uuid}`);
  const aiStoryImageKey = localStorage.getItem(`aiStoryImage-${item.uuid}`);

  console.log(`processed key for item ${item.product_name}:`, processedKey);
  console.log(`aiStoryImage key for item ${item.product_name}:`, aiStoryImageKey);

  const aiStoryData = React.useMemo(() => {
    try {
      const data = localStorage.getItem(`aiStoryData-${item.uuid}`);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error loading AI story data:', error);
      return null;
    }
  }, [item.uuid]);

  const isAIStory = React.useMemo(() => {
    // Check multiple indicators
    console.log('siStoryData:', aiStoryData);
    console.log('aiStoryImage:', aiStoryImageKey);
    console.log('item.is_Ai:', item?.product?.is_ai);
    if (aiStoryData) return true;
    if (aiStoryImageKey) return true;
    
    // Check if product has AI story indicator
    if (item?.product?.is_ai === true) return true;
    
    // Check if product type indicates AI story
    // You might have a specific product_type for AI stories
    // Example: if (item.product_type === 3) return true;
    
    // Check product slug or name for AI story indicators
    if (item.product_slug && item.product_slug.includes('ai-story')) return true;
    if (item.product_name && typeof item.product_name === 'string' && 
        (item.product_name.toLowerCase().includes('ai') || 
         item.product_name.toLowerCase().includes('story'))) return true;
    
    return false;
  }, [aiStoryData, aiStoryImageKey, item?.product?.is_ai, item.product_slug, item.product_name]);

  const getImageKeyForPreview = () => {
    // First check if we have a dedication image key
    if (processedKey) return processedKey;
    
    // If no dedication image but it's an AI story with AI image as dedication
    if (isAIStory && aiStoryImageKey) {
      return aiStoryImageKey;
    }
    
    return null;
  };

  const previewKey = getImageKeyForPreview();
  console.log(`preview key for ${item.product_name}: `, previewKey);

  const imagePrice =
  settingsInCart?.image_price?.amount !== undefined
    ? settingsInCart.image_price.amount
    : 0

  React.useEffect(() => {
    if (!previewKey) {
      setSignedUrl(null);
      return;
    }

    let isMounted = true;
    
    const fetchImage = async () => {
      try {
        setFetchingImage(true);
        if (previewKey) {
          const response = await GetDedicationImage(previewKey);
          const url = response?.data?.url;
          if (isMounted) setSignedUrl(url);
        } else if (aiStoryImageKey) {
          const response = await GetDedicationImage(aiStoryImageKey);
          const url = response?.data?.url;
          if (isMounted) setSignedUrl(url);
        }
        setFetchingImage(false);
      } catch (error) {
        console.error("Error fetching dedication image:", error);
        if (isMounted) setSignedUrl(null);
        setFetchingImage(false);
      } finally {
        setFetchingImage(false);
      }
    };

    fetchImage();

    return () => {
      isMounted = false;
    };
  }, [item.uuid]);

  const router = useRouter()
  const {t} = useTranslation('alerts');
  const alerts = allAlerts[locale as keyof typeof allAlerts] || allAlerts?.ar;
  const cartDispatch = useCartDispatch()
  const { openModal } = usePersonalizeModal()
  const {openModal : openNotebookModal} = useNotebookModal()
  const { openAIModal: openAIPersonalizeModal } = useAIPersonalizeModal()
  const [showPackagingInfo, setShowPackagingInfo] = React.useState(cart.shipping_country === 'IL' || cart.shipping_country == 'Israel'  ? true : false);

  // React.useEffect(()=>{
  //   console.log('country code in cart:', cart.shipping_country);
  //   console.log('show packaging info:', showPackagingInfo);
  // }, [])
  
  const duplicateItem = (item: Item) => {
    // Generate a new UUID for the duplicated item
    const newUuid = uuidv4();

    // Copy the stored image from localStorage
    const oldImageUrl = localStorage.getItem(`dedicationImageUrl-${item.uuid}`);
    if (oldImageUrl) {
      localStorage.setItem(`dedicationImageUrl-${newUuid}`, oldImageUrl);
    }

    const oldAIStoryImageKey = localStorage.getItem(`aiStoryImage-${item.uuid}`);
    if (oldAIStoryImageKey) {
      localStorage.setItem(`aiStoryImage-${newUuid}`, oldAIStoryImageKey);
    }

    const oldAIStoryData = localStorage.getItem(`aiStoryData-${item.uuid}`);
    if (oldAIStoryData) {
      localStorage.setItem(`aiStoryData-${newUuid}`, oldAIStoryData);
    }

    // Create new duplicated item with new UUID and same image path
    const new_item = {
      ...item,
      uuid: newUuid,
      image: item.image,
      is_ai: item?.product?.is_ai,
    };

    cartDispatch(addItem(new_item));

    toast.custom((tel) => (
      <CustomToast t={tel} message={t('cart_item_duplicated')} type="success" />
    ));
  };

  const deleteCartItem = async (id: string, product_id: string) => {
    await cartDispatch(removeItem(id));
    await track({ event_name: "remove_from_cart", metadata: {product_id: product_id} });
    toast.custom((t) => <CustomToast t={t} message={alerts.cart_item_removed} type='success' />);
  }

  // For Notebook only
  const [isOpen, setIsNotebookOpen] = React.useState(false)
  const [inputName, setInputName] = React.useState(item?.replace_name)
  const [inputLanguage, setInputLanguage] = React.useState<
    keyof LanguageTranslations | undefined
  >(item.language)
  const [qty, setQty] = React.useState(item.quantity)

  const demoName =
  typeof item?.product?.demo_name === 'object'
    ? item?.product?.demo_name?.[locale === 'ar' ? 'ar' : 'en']
    : item?.demo_name

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();

    console.log('handle edit click triggered');
    console.log('value of isAIStory:', isAIStory);
    
    if (item.product_type === 1) {
      // Check if this is an AI story
      if (isAIStory) {
        console.log('the story is AI');
        // Open AI personalize modal for AI stories
        console.log('opening ai personalize modal');
        openAIPersonalizeModal({
          product: {...item},
          inputs: item?.inputs,
          isEdit: '1',
        });
      } else {
        // Open regular personalize modal for non-AI stories
        console.log('opening simple personalize modal');
        openModal({
          product: {...item},
          inputs: item?.inputs,
          isEdit: '1',
        });
      }
    } else if (item.product_type == 2) {
      openNotebookModal({
        product: {...item},
        inputs: item?.inputs,
        isEdit: '1'
      });
    }
  };
  
  return (
    <>
      <div className={`lg:hidden ${isItemInFloatingCart ? 'mt-0' : 'mt-6'} w-full`}>
        <CartItemDisclosure
          item={item}
          setIsNotebookOpen={setIsNotebookOpen}
          index={index}
          displayButtonsIcon={isItemInFloatingCart}
        />
      </div>
      
      {showPreview && (
        <div className="fixed inset-0 z-[999] bg-black bg-opacity-70 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-lg max-w-[40%] max-h-[80vh] p-4 overflow-hidden">
            <button
              onClick={() => setShowPreview(false)}
              className="absolute top-2 right-2 text-primary w-12 h-12"
            >
              <CrossIcon />
            </button>

            {fetchingImage ? (
              <div className="flex flex-col items-center justify-center p-6 mt-6">
                <div className="w-8 h-8 border-4 border-gray-300 border-t-primary rounded-full animate-spin"></div>
                <p className="text-sm text-gray-600 mt-3">
                  {tCart('loadingPreviewText')}
                </p>
              </div>
            ) : signedUrl ? (
              <ImageWithLoader src={signedUrl} />
            ) : (
              <p className="text-sm text-gray-600 mt-6">{tCart('unableToLoadThePreview')}</p>
            )}
          </div>
        </div>
      )}
      
      <div className="cart-item hidden lg:flex bg-white py-4 px-4 rounded-md mt-4" dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
        <div className="lg:basis-[15%]">
          <div className="flex items-center justify-between">
            <div className="cart-item-image basis-4/12 lg:basis-auto lg:w-[120px]">
              <Image
                src={process.env.NEXT_PUBLIC_CDN_URL + '/' + item.image}
                alt="product-image"
                width={150}
                height={150}
                className="rounded-md w-full h-[120px] object-cover"
              />
            </div>
          </div>
          
          <div className="flex items-center lg:hidden">
            <button className="bg-red w-11 h-11 rounded-full hidden lg:flex items-center justify-center">
              <PriceIcon/>
            </button>
            <h2 className="mt-4 text-red text-xl font-semibold">
              {currencySymbol}{item.price}
            </h2>
          </div>
        </div>
        
        {item['product_type'] ? (
          <div className={`cart-item-content w-full ${locale === 'ar' ? 'lg:mr-4':'lg:ml-4'}`} dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
            <h1 className="font-semibold text-2xl my-2 text-primary">
              {demoName ? demoName.replace('{basmti}', item?.replace_name) : ''}
            </h1>
            
            {item.product_type == 2 && (
              <>
                <div className="mt-3 lg:flex">
                  <p className="text-gray-400">
                    {tCart('cartItemDetailsChildName')}
                  </p>
                  <p className={`text-primary ${locale === 'ar' ? 'lg:mr-2' : 'lg:ml-2'}`}>{item?.replace_name}</p>
                </div>

                <div className="mt-3 lg:flex">
                  <p className="text-gray-400">
                    {tCart('cartItemDetailsNotebookType')}
                  </p>
                  <p className={`text-primary ${locale === 'ar' ? 'lg:mr-2' : 'lg:ml-2'}`}>
                    {item.language ? language_translations[item.language] : ''}
                  </p>
                </div>

                <div className="mt-3 lg:flex">
                  <p className="text-gray-400">
                    {tCart('cartItemDetailsNotebookQuantity')}
                  </p>
                  <p className={`text-primary ${locale === 'ar' ? 'lg:mr-2' : 'lg:ml-2'}`}>
                    {item.quantity} {tCart('cartItemDetailsNotebookQuantityText')}
                  </p>
                </div>

                {item.product_type == 2 && previewKey && (
                  <div className="lg:flex">
                    <p className="text-gray-400">
                      {tCart('cartItemDetailsChildImage')}
                    </p>
                    <div className={`text-primary ${locale === 'ar' ? 'lg:mr-2' : 'lg:ml-2'}`}>
                      <button
                        onClick={() => setShowPreview(true)}
                        className="text-primary underline underline-offset-8 hover:text-secondary transition-colors"
                      >
                        {tCart('cartItemDetailsImagePreviewText')} ({imagePrice}{currencySymbol})
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {item.product_type == 1 && (
              <>
                <div className="lg:flex">
                  <p className="text-gray-400">{tCart('cartItemDetailsChildName')}</p>
                  <p className={`text-primary ${locale === 'ar' ? 'lg:mr-2' : 'lg:ml-2'}`}>
                    {item?.replace_name} ({GenderMap(item.gender ?? '', locale)})
                  </p>
                </div>

                <div className="mt-3 lg:flex">
                  <p className="text-gray-400">
                    {tCart('cartItemDetialsDedication')}
                  </p>
                  <p className={`text-primary ${locale === 'ar' ? 'lg:mr-2' : 'lg:ml-2'}`}>
                    {Array.from(item.dedication || "").slice(0, 100).join("")}
                    {item.dedication && item.dedication.length > 100 ? "..." : ""}
                  </p>
                </div>

                <div className="mt-3 lg:flex">
                  <p className="text-gray-400">
                    {tCart('cartItemDetialsCover')}
                  </p>
                  <p className={`text-primary ${locale === 'ar' ? 'lg:mr-2' : 'lg:ml-2'}`}>
                    {item.cover?.name ? 
                        (typeof item.cover.name === 'string' ? 
                          item.cover.name : 
                          (locale === 'ar' ? item.cover.name?.ar : item.cover.name?.en)
                        ) : ''
                      } ({
                        item.cover?.price ? 
                          (typeof item.cover.price === 'string' ? 
                            item.cover.price : 
                            item.cover.price?.amount
                          ) : ''
                      }{currencySymbol})
                  </p>
                </div>

                {showPackagingInfo && (
                  <div className="mt-3 lg:flex">
                    <p className="text-gray-400">
                      {tCart('cartItemDetialsPackaging')}
                    </p>
                    <p className={`text-primary ${locale === 'ar' ? 'lg:mr-2' : 'lg:ml-2'}`}>
                      {item.packaging?.name === 'Packaging' ? `${tCart('packagingAdded')}` : `${tCart('packagingNotAdded')}`} ({item.packaging?.price ?? 0}{currencySymbol})
                    </p>
                  </div>
                )}

                {item.product_type == 1 && previewKey && (
                  <div className="lg:flex">
                    <p className="text-gray-400">{tCart('cartItemDetailsChildImage')}</p>
                    <div className={`text-primary ${locale === 'ar' ? 'lg:mr-2' : 'lg:ml-2'}`}>
                      {previewKey ? (
                        <button
                          onClick={() => setShowPreview(true)}
                          className="text-primary underline underline-offset-8 hover:text-secondary transition-colors"
                        >
                          {tCart('cartItemDetailsImagePreviewText')} ({imagePrice}{currencySymbol})
                        </button>
                      ) : (
                        <span>{tCart('cartItemDetailsImageNotAdded')} (0{currencySymbol})</span>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="buttons lg:max-w-[80%] lg:flex flex-row-reverse justify-between">
              <Button
                type="button"
                bg="primary"
                onClick={() => duplicateItem(item)}
                classes={['w-full', 'mt-4', 'lg:mr-4', 'text-sm', 'py-3']}
              >
                {`${tCart('duplicateButtonText')}`}
              </Button>
              
              <Button
                style="outline"
                type="button"
                bg="primary"
                classes={[
                  'w-full',
                  'flex',
                  'mt-4',
                  'text-sm',
                  'justify-center',
                  'lg:mr-4',
                  'items-center',
                  'py-2',
                ]}
                onClick={handleEditClick}
              >
                <>
                  <span className={`${locale === 'ar' ? 'ml-2' : 'mr-2'}`}>
                    <EditIcon/>
                  </span>
                  {tCart('editButtonText')}
                </>
              </Button>
            </div>
          </div>
        ) : (
          <div className={`cart-item-content w-full ${locale === 'ar' ? 'lg:mr-4': 'lg:ml-4'}`} dir={`${locale === 'ar' ? 'rtl' : 'ltr'}`}>
            <h1 className="font-semibold text-2xl my-2 text-primary">
              {demoName ? demoName.replace('{basmti}', item?.replace_name) : ''}
            </h1>
            
            <div className="lg:flex">
              <p className="text-gray-400">{tCart('cartItemDetailsChildName')}</p>
              <p className={`text-primary ${locale === 'ar' ? 'lg:mr-2' : 'lg:ml-2'}`}>
                {item?.replace_name} ({GenderMap(item.gender ?? '', locale)})
              </p>
            </div>

            <div className="mt-3 lg:flex">
              <p className="text-gray-400">{tCart('cartItemDetialsDedication')}</p>
              <p className={`text-primary ${locale === 'ar' ? 'lg:mr-2' : 'lg:ml-2'}`}>
                {Array.from(item.dedication || "").slice(0, 100).join("")}
                {item.dedication && item.dedication.length > 100 ? "..." : ""}
              </p>
            </div>

            <div className="mt-3 lg:flex">
              <p className="text-gray-400">{tCart('cartItemDetialsCover')}</p>
              <p className={`text-primary ${locale === 'ar' ? 'lg:mr-2' : 'lg:ml-2'}`}>
                {item.cover?.name ? 
                        (typeof item.cover.name === 'string' ? 
                          item.cover.name : 
                          (locale === 'ar' ? item.cover.name?.ar : item.cover.name?.en)
                        ) : ''
                      } ({
                        item.cover?.price ? 
                          (typeof item.cover.price === 'string' ? 
                            item.cover.price : 
                            item.cover.price?.amount
                          ) : ''
                      }{currencySymbol})
              </p>
            </div>

            {showPackagingInfo && (
              <div className="mt-3 lg:flex">
                <p className="text-gray-400">{tCart('cartItemDetialsPackaging')}</p>
                <p className={`text-primary ${locale === 'ar' ? 'lg:mr-2' : 'lg:ml-2'}`}>
                  {item.packaging?.name === 'Packaging' ? `${t('packagingAdded')}` : `${t('packagingNotAdded')}`} ({item.packaging?.price ?? 0}{currencySymbol})
                </p>
              </div>
            )}
            
            <div className="buttons lg:max-w-[80%] lg:flex flex-row-reverse justify-between">
              <Button
                type="button"
                bg="primary"
                onClick={() => duplicateItem(item)}
                classes={['w-full', 'mt-4', 'lg:mr-4', 'text-sm', 'py-3']}
              >
                {`${t('duplicateButtonText')}`}
              </Button>
              
              <Button
                style="outline"
                type="button"
                bg="primary"
                classes={[
                  'w-full',
                  'flex',
                  'mt-4',
                  'text-sm',
                  'justify-center',
                  'lg:mr-4',
                  'items-center',
                  'py-2',
                ]}
                onClick={handleEditClick}
              >
                <>
                  <span className="ml-2">
                    <EditIcon/>
                  </span>
                  {tCart('editButtonText')}
                </>
              </Button>
            </div>
          </div>
        )}

        <div className="hidden lg:flex lg:flex-col items-end justify-between mr-auto">
          <h2 className="mt-12 text-red text-xl font-semibold">
            <div className="flex flex-col items-center justify-center">
              <p>{currencySymbol}{item.total}</p>
            </div>
          </h2>
          <button
            className="bg-red w-11 h-11 rounded-full hidden lg:flex items-center justify-center"
            onClick={() => deleteCartItem(item.uuid, item?.product?.id)}
          >
            <DeleteIcon/>
          </button>
        </div>
      </div>

      {item.product_type == 2 && (
        <NotebookDetailsModal
          isOpen={isOpen}
          setIsNotebookOpen={setIsNotebookOpen}
          slug={item.product_slug}
          inputLanguage={inputLanguage}
          isEdit={true}
          setInputLanguage={setInputLanguage}
          inputName={inputName}
          qty={qty ?? 1}
          setQty={setQty}
          uuid={item.uuid}
          setInputName={setInputName}
        />
      )}
    </>
  )
}

function ImageWithLoader({ src }: { src: string }) {
  const {t:tCart} = useTranslation('cart');
  const [loading, setLoading] = React.useState(true);

  return (
    <div className="relative w-full h-full flex items-center justify-center mt-12">
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-primary rounded-full animate-spin"></div>
          <p className="text-sm text-gray-600 mt-3">
            {tCart('imageLoadingText')}
          </p>
        </div>
      )}
      <img
        src={src}
        alt="Product Preview"
        className={`w-full h-full object-contain rounded-md transition-opacity duration-300 ${
          loading ? 'opacity-0' : 'opacity-100'
        }`}
        onLoad={() => setLoading(false)}
        onError={(e) => {
          console.error("Image failed to load:", e);
          setLoading(false);
        }}
      />
    </div>
  );
}